import type { FC } from 'react';
import { Alert } from '@interstate/components/Alert';
import { Typography } from '@interstate/components/Typography';
import { CREDIT_CONSENT_BANNER_MESSAGE, CREDIT_CONSENT_BANNER_TITLE } from './creditConsent.constants';
import { formatSentAt } from './creditConsent.utils';

export type CreditConsentBannerProps = {
    /** ISO timestamp of the save that granted consent. Omitted renders the message with no stamp. */
    sentAt?: string;
    /** IANA zone the stamp is rendered in, and its display label. Defaults to Eastern. */
    timeZone?: string;
    zoneLabel?: string;
    /** When false, only the title renders — no message and no sent stamp. */
    showMessage?: boolean;
    'data-testid'?: string;
};

/**
 * Confirms that consent was captured and the customer's SPD is bound for Dealertrack/Unify.
 *
 * `disableSuccessFade` is deliberate: Interstate fades success alerts out after 5s, and this design
 * has no dismiss control, so without it a compliance confirmation would silently disappear with no
 * way to bring it back.
 */
export const CreditConsentBanner: FC<CreditConsentBannerProps> = ({
    sentAt,
    timeZone,
    zoneLabel,
    showMessage = true,
    'data-testid': dataTestId = 'credit-consent-banner'
}) => {
    const stamp = formatSentAt(sentAt, timeZone, zoneLabel);

    return (
        <Alert type="success" disableSuccessFade sx={{ marginBottom: '1rem' }} data-testid={dataTestId}>
            <Typography variant="body-sm" tag="div" sx={{ fontWeight: 'bold' }}>
                {CREDIT_CONSENT_BANNER_TITLE}
            </Typography>
            {showMessage && (
                <Typography variant="body-sm" tag="div" data-testid={`${dataTestId}-message`}>
                    {stamp ? `${CREDIT_CONSENT_BANNER_MESSAGE} (Sent ${stamp})` : CREDIT_CONSENT_BANNER_MESSAGE}
                </Typography>
            )}
        </Alert>
    );
};
